import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';

export const usePagePermissions = () => {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPermissions = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.get(`/page-permissions/users/${user.id}`);
      if (response.data.success) {
        setPermissions(response.data.data || []);
        setError(null);
      }
    } catch (err) {
      console.error('Failed to fetch page permissions:', err);
      setError(err.message);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const canViewPage = useCallback((pageName) => {
    if (!user) return false;
    if (user.role === 'admin') return true; // Admins can view all pages

    const permission = permissions.find(p => p.page_name === pageName);
    return permission?.can_view === 1 || permission?.can_view === true;
  }, [permissions, user]);

  const canEditPage = useCallback((pageName) => {
    if (!user) return false;
    if (user.role === 'admin') return true;

    const permission = permissions.find(p => p.page_name === pageName);
    return (permission?.can_edit === 1 || permission?.can_edit === true) && 
           (permission?.can_view === 1 || permission?.can_view === true);
  }, [permissions, user]);

  const canDeletePage = useCallback((pageName) => {
    if (!user) return false;
    if (user.role === 'admin') return true;

    const permission = permissions.find(p => p.page_name === pageName);
    return (permission?.can_delete === 1 || permission?.can_delete === true) && 
           (permission?.can_view === 1 || permission?.can_view === true);
  }, [permissions, user]);

  const getViewablePages = useCallback(() => {
    if (!user) return [];
    if (user.role === 'admin') return permissions;
    
    return permissions.filter(p => p.can_view === 1 || p.can_view === true);
  }, [permissions, user]);

  return {
    permissions,
    loading,
    error,
    canViewPage,
    canEditPage,
    canDeletePage,
    getViewablePages,
    refetchPermissions: fetchPermissions
  };
};
